import classNames from 'classnames';
import Discount from 'components/discount';
import DiscountTermsModal from 'components/discountTermsModal';
import { SizeMeProps, withSize } from 'hoc/withSize';
import { Discount as ClientDiscount } from 'hooks/useClientDiscounts';
import { Discount as IDiscount } from 'hooks/usePropertyDiscount';
import DefaultLink from 'next/link';
import { FC, ReactNode, useContext, useEffect, useState } from 'react';
import type { Venue as VenueModel } from 'types/domain';
import { useTranslation } from 'utils/i18n';
import { TABLET_WIDTH } from '../../../constants/breakpoints';
import useFavourite from '../../../hooks/useFavourite';
import { AuthContext } from '../../../state/auth';
import { filteredKeyFacets } from '../../../utils/filters';
import AddToBookingButton from '../../addToBookingButton';
import FloorplansButton from '../../floorplansButton';
import { FavouriteButton } from '../../shared/favouriteButton';
import FeatureIconBase from '../../shared/featureIcon';
import SpacePrice from '../../spacePrice';

interface FeatureIconProps {
  type?: string;
  name?: string;
  additionalText?: string;
  icon?: string;
  iconAltText?: string;
  displaySvgIcon?: boolean;
}

const FeatureIcon = FeatureIconBase as unknown as FC<FeatureIconProps>;

interface LinkProps {
  href: string;
  children?: ReactNode;
}

interface Facet {
  isKey: boolean;
  name: string;
  icon: string;
  iconAltText: string;
}

const Link: FC<LinkProps> = ({ children, ...props }) => {
  return <DefaultLink {...props}>{children}</DefaultLink>;
};

type Venue = Pick<VenueModel, 'id' | 'name'>;

interface Props {
  addedToBooking: boolean;
  selected?: boolean;
  disabled: boolean;
  id: number;
  image: string;
  name: string;
  venue: Venue;
  clinical: boolean;
  price: number;
  capacity: number;
  floor: string;
  floorplan?: string | null;
  handleAddToBookingClick: () => void;
  size: SizeMeProps;
  facets: Array<Facet>;
  linkComponent?: typeof Link;
  discount?: IDiscount;
  discounts?: Array<ClientDiscount>;
  categoryIds?: Array<number>;
}

const SpaceListItem: FC<Props> = ({
  addedToBooking = false,
  disabled = false,
  id,
  image,
  name,
  venue,
  clinical = false,
  price,
  capacity = null,
  floor = null,
  floorplan = null,
  handleAddToBookingClick,
  size,
  facets = [],
  discount = undefined,
  discounts = [],
  categoryIds,
  linkComponent: LinkComponent = Link,
}) => {
  const { t } = useTranslation('common');
  const { loggedIn } = useContext(AuthContext);
  const [favourited, toggleFavourite] = useFavourite('spaces', id);
  const listItemTypeClasses = classNames({
    'space-list-item': true,
    'space-list-item--clinical': clinical,
    'space-list-item--non-clinical': !clinical,
  });

  const clinicalClasses = classNames({
    'clinical-indicator': true,
    'clinical-indicator--clinical': clinical,
    'clinical-indicator--non-clinical': !clinical,
  });

  const priceClasses = classNames({
    'space-list-item__price': true,
    'space-list-item__price--loggedin': loggedIn,
  });

  const [discountTermsModalIsOpen, setDiscountTermsModalIsOpen] =
    useState(false);

  const displayDiscount = () => {
    if (categoryIds!.length === 0) {
      return false;
    }

    if (discount!.roomTypeIds.length) {
      return discount!.roomTypeIds.includes(categoryIds![0]);
    }

    return discount;
  };

  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    const anchorEl = document.getElementById(anchor);

    if (anchor && anchorEl) {
      anchorEl.scrollIntoView(false);
    }
  }, []);

  return (
    <li key={id} className={listItemTypeClasses} data-cy="room">
      <p className="space-list-item__image-container">
        {image && (
          <img
            src={image}
            alt={`${t('space_list_item.photograph_alt')} ${name}, ${
              venue.name
            }`}
          />
        )}
        {!image && <span>{t('shared.picture_coming_soon')}</span>}

        {discount && displayDiscount() && (
          <>
            <Discount
              text={t('discounts.save', {
                percent: discount.amount,
              })}
              rotated
              size="small"
              onClick={() => setDiscountTermsModalIsOpen(true)}
            />

            <DiscountTermsModal
              isOpen={discountTermsModalIsOpen}
              onClose={() => setDiscountTermsModalIsOpen(false)}
              propertyOrRoomName={name}
              discounts={discounts}
            />
          </>
        )}
      </p>

      <div className="space-list-item__details-container">
        <div className="space-list-item__details">
          <h4 className={clinicalClasses}>
            {clinical ? t('shared.clinical') : t('shared.non_clinical')}
          </h4>
          <h3 className="space-list-item__details__name">
            <LinkComponent href={`${t('navigation.spaces.url')}/${id}`}>
              {name}
            </LinkComponent>
          </h3>
          <h5 className="space-list-item__details__venue-name">
            <LinkComponent href={`${t('navigation.venues.url')}/${venue.id}`}>
              {venue.name}
            </LinkComponent>
          </h5>
          <ul className="space-list-item__details__feature-list">
            <FloorplansButton
              venue={venue}
              space={{
                id,
                floor: floor as string,
                floorplan: floorplan ?? undefined,
              }}
              wrapper="li"
            />
            {filteredKeyFacets(facets).map(facet => (
              <li key={facet.name}>
                <FeatureIcon
                  name={facet.name}
                  icon={facet.icon}
                  iconAltText={facet.iconAltText}
                  displaySvgIcon={false}
                />
              </li>
            ))}
            {!clinical && capacity && (
              <li className="space-list-item__details__feature-list__feature">
                <FeatureIcon
                  type="room-capacity"
                  name={t('feature_icon.room_capacity', {
                    num: capacity,
                  })}
                  additionalText={String(capacity)}
                  displaySvgIcon
                />
              </li>
            )}
          </ul>
        </div>

        <div className="space-list-item__booking">
          <SpacePrice price={price} className={priceClasses} />
          <AddToBookingButton
            className="space-list-item__book-btn"
            onClick={handleAddToBookingClick}
            added={addedToBooking}
            enabled={!disabled}
            desktop={(size.width ?? 0) >= TABLET_WIDTH}
          />
        </div>

        {loggedIn && (
          <FavouriteButton
            favouritedState={favourited}
            type="spaces"
            handleFavouriteButtonClick={toggleFavourite}
          />
        )}
      </div>
    </li>
  );
};

// Export unwrapped component for testing
export { SpaceListItem };

export default withSize()(SpaceListItem);
